"use client";

import { AnalyticsSkeleton } from "@/components/ui/skeleton";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberTicker } from "@/components/ui/number-ticker";
import { PageHeader } from "@/components/page-header";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ModelAgentRow,
  ModelAnalyticsData,
  ModelAnalyticsRange as Range,
  ModelStatRow,
  ModelSurfaceRow,
  ModelTrendPoint,
  ModelUserRow,
} from "@/types/model-analytics";

// ─── Constants ─────────────────────────────────────────────────────────────────

const RANGES: { value: Range; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 2 weeks" },
  { value: "30d", label: "Last month" },
  { value: "90d", label: "Last 3 months" },
  { value: "all", label: "All time" },
];

type TrendMetric = "chats" | "messages" | "tokens" | "cost";

const TREND_METRICS: { value: TrendMetric; label: string }[] = [
  { value: "chats", label: "Chats" },
  { value: "messages", label: "Messages" },
  { value: "tokens", label: "Tokens" },
  { value: "cost", label: "Cost" },
];

// Evenly-spaced steps on the single-hue --chart-* ramp stay tellable apart at
// line width; adjacent steps read as near-identical (same reasoning as
// agent-analytics-panel).
const MODEL_LINE_COLORS = [
  "var(--chart-1)",
  "var(--chart-3)",
  "var(--chart-5)",
  "var(--chart-2)",
  "var(--chart-4)",
];

const MAX_TREND_MODELS = 6;

// ─── Formatting ────────────────────────────────────────────────────────────────

const compactNumber = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const fmtTokens = (n: number) => compactNumber.format(n);

/** null = "unpriced" — render distinctly, never as $0. */
function fmtCost(cost: number | null): string {
  if (cost === null) return "—";
  if (cost > 0 && cost < 0.01) return "<$0.01";
  return `$${cost.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function agentKindLabel(row: ModelAgentRow): string {
  if (row.kind === "managed") return "Managed";
  if (row.kind === "general") return "No agent";
  return row.agentType === "knowledge" ? "Knowledge" : "Custom";
}

/** Query-string tail for the analytics fetch. */
function rangeParams(r: Range, dateRange: DateRange | undefined): string {
  if (r === "custom" && dateRange?.from) {
    const from = dateRange.from.toISOString();
    const to = (dateRange.to ?? dateRange.from).toISOString();
    return `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }
  return `&range=${r}`;
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  display,
  sub,
}: {
  label: string;
  value?: number;
  /** Pre-formatted value (e.g. cost / compact tokens) instead of a ticker. */
  display?: string;
  sub?: string;
}) {
  return (
    <Card size="sm" className="gap-3">
      <CardContent className="pt-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="mt-1 text-3xl font-semibold text-foreground tabular-nums">
          {display ?? <NumberTicker value={value ?? 0} />}
        </p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Tables ────────────────────────────────────────────────────────────────────

const modelColumns: DataTableColumn<ModelStatRow>[] = [
  {
    header: "Model",
    render: (row) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{row.name}</p>
        <p className="truncate font-mono text-xs text-muted-foreground">{row.model}</p>
      </div>
    ),
  },
  {
    header: "Provider",
    width: "w-[110px]",
    render: (row) => <Badge variant="outline" className="text-[10px] capitalize">{row.provider}</Badge>,
  },
  {
    header: "Chats",
    width: "w-[90px]",
    align: "right",
    sortKey: "chats",
    sortValue: (row) => row.chats,
    render: (row) => row.chats,
  },
  {
    header: "Messages",
    width: "w-[100px]",
    align: "right",
    sortKey: "messages",
    sortValue: (row) => row.messages,
    render: (row) => row.messages,
  },
  {
    header: "Users",
    width: "w-[90px]",
    align: "right",
    sortKey: "users",
    sortValue: (row) => row.users,
    render: (row) => row.users,
  },
  {
    header: "Tokens",
    width: "w-[100px]",
    align: "right",
    sortKey: "tokens",
    sortValue: (row) => row.tokens,
    render: (row) => fmtTokens(row.tokens),
  },
  {
    header: "Est. cost",
    width: "w-[110px]",
    align: "right",
    sortKey: "cost",
    sortValue: (row) => row.cost ?? -1,
    render: (row) => fmtCost(row.cost),
  },
];

const agentColumns: DataTableColumn<ModelAgentRow>[] = [
  {
    header: "Agent",
    render: (row) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{row.name}</p>
        <Badge variant="outline" className="mt-0.5 text-[10px]">{agentKindLabel(row)}</Badge>
      </div>
    ),
  },
  {
    header: "Model",
    width: "w-[180px]",
    render: (row) => <span className="truncate font-mono text-xs">{row.model}</span>,
  },
  {
    header: "Events",
    width: "w-[90px]",
    align: "right",
    sortKey: "events",
    sortValue: (row) => row.events,
    render: (row) => row.events,
  },
  {
    header: "Tokens",
    width: "w-[100px]",
    align: "right",
    sortKey: "tokens",
    sortValue: (row) => row.tokens,
    render: (row) => fmtTokens(row.tokens),
  },
  {
    header: "Est. cost",
    width: "w-[110px]",
    align: "right",
    sortKey: "cost",
    sortValue: (row) => row.cost ?? -1,
    render: (row) => fmtCost(row.cost),
  },
];

const surfaceColumns: DataTableColumn<ModelSurfaceRow>[] = [
  {
    header: "Where",
    render: (row) => <span className="truncate font-medium text-foreground">{row.surface}</span>,
  },
  {
    header: "Model",
    width: "w-[180px]",
    render: (row) => <span className="truncate font-mono text-xs">{row.model}</span>,
  },
  {
    header: "Events",
    width: "w-[90px]",
    align: "right",
    sortKey: "events",
    sortValue: (row) => row.events,
    render: (row) => row.events,
  },
  {
    header: "Tokens",
    width: "w-[100px]",
    align: "right",
    sortKey: "tokens",
    sortValue: (row) => row.tokens,
    render: (row) => fmtTokens(row.tokens),
  },
  {
    header: "Est. cost",
    width: "w-[110px]",
    align: "right",
    sortKey: "cost",
    sortValue: (row) => row.cost ?? -1,
    render: (row) => fmtCost(row.cost),
  },
];

const userColumns: DataTableColumn<ModelUserRow>[] = [
  {
    header: "User",
    render: (row) => (
      <div className="flex items-center gap-1.5">
        <span className="truncate">{row.name}</span>
        {row.isDeveloper && <Badge variant="secondary" className="text-[10px]">Developer</Badge>}
      </div>
    ),
  },
  {
    header: "Top model",
    width: "w-[170px]",
    render: (row) => <span className="truncate font-mono text-xs">{row.topModel ?? "—"}</span>,
  },
  {
    header: "Used in",
    render: (row) => (
      <div className="flex flex-wrap gap-1">
        {row.surfaces.slice(0, 4).map((s) => (
          <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
        ))}
        {row.surfaces.length > 4 && (
          <Badge variant="secondary" className="text-[10px]">+{row.surfaces.length - 4}</Badge>
        )}
      </div>
    ),
  },
  {
    header: "Events",
    width: "w-[90px]",
    align: "right",
    sortKey: "events",
    sortValue: (row) => row.events,
    render: (row) => row.events,
  },
  {
    header: "Tokens",
    width: "w-[100px]",
    align: "right",
    sortKey: "tokens",
    sortValue: (row) => row.tokens,
    render: (row) => fmtTokens(row.tokens),
  },
  {
    header: "Est. cost",
    width: "w-[110px]",
    align: "right",
    sortKey: "cost",
    sortValue: (row) => row.cost ?? -1,
    render: (row) => fmtCost(row.cost),
  },
];

// ─── Main component ────────────────────────────────────────────────────────────

type ModelAnalyticsPanelProps = {
  onBack?: () => void;
};

export function ModelAnalyticsPanel({ onBack }: ModelAnalyticsPanelProps) {
  const [range, setRange] = useState<Range>("30d");
  const [data, setData] = useState<ModelAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Off by default: usage metrics exclude superadmin ("developer") activity
  // unless the admin viewing this page explicitly asks to see it.
  const [includeDevelopers, setIncludeDevelopers] = useState(false);

  const [trendMetric, setTrendMetric] = useState<TrendMetric>("messages");

  const load = useCallback(
    async (r: Range, dateRange: DateRange | undefined, includeDevs: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const url =
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-model-analytics?includeDevelopers=${includeDevs}` +
          rangeParams(r, dateRange);
        const res = await fetch(url, {
          headers: {
            apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
        });

        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load model analytics.");
        setData(json as ModelAnalyticsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load model analytics.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (range === "custom") return; // only load custom when the user explicitly applies it
    void load(range, undefined, includeDevelopers);
  }, [load, range, includeDevelopers]);

  const periodLabel =
    range === "custom" && customDateRange?.from
      ? customDateRange.to
        ? `${customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${customDateRange.to.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
        : customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : RANGES.find((r) => r.value === range)?.label.toLowerCase() ?? "";

  // Models charted individually — the rest are aggregated into "Other" so
  // the chart stays readable if unexpected model ids show up in history.
  const trendModels = useMemo(() => {
    if (!data) return [];
    return data.models.slice(0, MAX_TREND_MODELS).map((m) => m.model);
  }, [data]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = {};
    trendModels.forEach((model, i) => {
      config[model] = {
        label: data?.models.find((m) => m.model === model)?.name ?? model,
        color: MODEL_LINE_COLORS[i % MODEL_LINE_COLORS.length],
      };
    });
    if ((data?.models.length ?? 0) > MAX_TREND_MODELS) {
      config.__other = { label: "Other", color: "var(--muted-foreground)" };
    }
    return config;
  }, [trendModels, data]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const pick = (p: ModelTrendPoint) => p[trendMetric];
    return data.trend.map((p) => {
      const counts = pick(p);
      const row: Record<string, number | string> = { label: p.label };
      let other = 0;
      for (const [model, value] of Object.entries(counts)) {
        if (trendModels.includes(model)) row[model] = value;
        else other += value;
      }
      for (const model of trendModels) {
        if (!(model in row)) row[model] = 0;
      }
      if ((data.models.length ?? 0) > MAX_TREND_MODELS) row.__other = other;
      return row;
    });
  }, [data, trendMetric, trendModels]);

  const chartSeries = useMemo(
    () =>
      (data?.models.length ?? 0) > MAX_TREND_MODELS
        ? [...trendModels, "__other"]
        : trendModels,
    [data, trendModels]
  );

  if (loading && !data) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Model Analytics" description="Which models are used, where, by whom, and what they cost" onBack={onBack} />
        <AnalyticsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Model Analytics" description="Which models are used, where, by whom, and what they cost" onBack={onBack} />
        <div className="flex flex-1 items-center justify-center px-6">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Model Analytics"
        description="Which models are used, where, by whom, and what they cost"
        onBack={onBack}
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-2 pr-1">
              <Switch
                id="model-analytics-include-developers"
                checked={includeDevelopers}
                onCheckedChange={setIncludeDevelopers}
              />
              <label htmlFor="model-analytics-include-developers" className="text-xs text-muted-foreground">
                Include Developers Usage
              </label>
            </div>

            <ToggleGroup
              type="single"
              value={range === "custom" ? "" : range}
              onValueChange={(v) => {
                if (!v) return;
                setCustomDateRange(undefined);
                setRange(v as Range);
              }}
              variant="outline"
              size="sm"
              className="flex-wrap"
            >
              {RANGES.map(({ value, label }) => (
                <ToggleGroupItem key={value} value={value}>
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={range === "custom" ? "default" : "outline"}
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <CalendarIcon className="size-3.5" />
                  {range === "custom" && customDateRange?.from ? periodLabel : "Custom"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={customDateRange}
                  onSelect={setCustomDateRange}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  captionLayout="dropdown"
                />
                <div className="flex justify-end gap-2 border-t border-border px-3 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setCustomDateRange(undefined);
                      setCalendarOpen(false);
                      setRange("30d");
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={!customDateRange?.from}
                    onClick={() => {
                      setCalendarOpen(false);
                      setRange("custom");
                      void load("custom", customDateRange, includeDevelopers);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="space-y-8 px-6 py-6">

          {/* ── Summary ───────────────────────────────────────────────────── */}
          <div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <KpiCard label="Models used" value={summary.modelsUsed} sub={periodLabel} />
              <KpiCard label="Chats" value={summary.chats} sub={periodLabel} />
              <KpiCard label="Messages" value={summary.messages} sub="assistant replies" />
              <KpiCard label="Active users" value={summary.activeUsers} sub={periodLabel} />
              <KpiCard label="Tokens" display={fmtTokens(summary.totalTokens)} sub="input + output" />
              <KpiCard
                label="Est. cost"
                display={fmtCost(summary.totalCost)}
                sub={summary.totalCost === null ? "cost data unavailable" : "priced rows only"}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {includeDevelopers
                ? `Includes activity from ${summary.developerCount} developer account${summary.developerCount === 1 ? "" : "s"}.`
                : `Excludes ${summary.developerCount} developer account${summary.developerCount === 1 ? "" : "s"} (superadmins). Toggle "Include Developers Usage" to see it.`}
              {" "}Also counted: {summary.managedSessions} managed agent session{summary.managedSessions === 1 ? "" : "s"} and {summary.otherJobs} background job{summary.otherJobs === 1 ? "" : "s"} (meetings, embeddings, parsing, utility calls).
            </p>
          </div>

          {/* ── Usage over time ───────────────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-2">
              <div>
                <CardTitle>Usage over time</CardTitle>
                <CardDescription>One line per model. Cost only includes priced rows.</CardDescription>
              </div>
              <ToggleGroup
                type="single"
                value={trendMetric}
                onValueChange={(v) => v && setTrendMetric(v as TrendMetric)}
                variant="outline"
                size="sm"
                className="flex-wrap"
              >
                {TREND_METRICS.map(({ value, label }) => (
                  <ToggleGroupItem key={value} value={value} className="text-xs">
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </CardHeader>
            <CardContent>
              {chartData.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No data for this period.
                </p>
              ) : (
                <ChartContainer config={chartConfig} className="h-70 aspect-auto">
                  <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                      allowDecimals={trendMetric === "cost"}
                      tickFormatter={(v: number) =>
                        trendMetric === "cost" ? `$${v}` : compactNumber.format(v)
                      }
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {chartSeries.map((model) => (
                      <Line
                        key={model}
                        dataKey={model}
                        stroke={chartConfig[model]?.color as string}
                        strokeWidth={2}
                        dot={false}
                        type="monotone"
                      />
                    ))}
                  </LineChart>
                </ChartContainer>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {chartSeries.map((model) => (
                  <span key={model} className="flex items-center gap-1.5">
                    <span
                      className="size-2.5 rounded-sm"
                      style={{ background: chartConfig[model]?.color as string }}
                    />
                    {String(chartConfig[model]?.label ?? model)}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ── Model usage ───────────────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Model usage</CardTitle>
              <CardDescription>
                Chats, assistant messages, unique users, tokens and estimated cost per model. &ldquo;—&rdquo; means no priced rows.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={modelColumns}
                data={data.models}
                getRowKey={(row) => row.model}
                searchFilter={(row, q) =>
                  row.model.toLowerCase().includes(q) || row.name.toLowerCase().includes(q)
                }
                searchPlaceholder="Search models…"
                itemLabel="model"
                emptyMessage="No model usage in this period."
              />
            </CardContent>
          </Card>

          {/* ── Agent breakdown ───────────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Agent breakdown</CardTitle>
              <CardDescription>
                Which agents drive each model&rsquo;s usage. &ldquo;No agent&rdquo; rows are plain chats without an agent attached.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={agentColumns}
                data={data.agents}
                getRowKey={(row) => `${row.kind}:${row.agentId ?? "none"}:${row.model}`}
                searchFilter={(row, q) =>
                  row.name.toLowerCase().includes(q) || row.model.toLowerCase().includes(q)
                }
                searchPlaceholder="Search agents or models…"
                itemLabel="row"
                emptyMessage="No agent usage in this period."
              />
            </CardContent>
          </Card>

          {/* ── Where models are used ─────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Where models are used</CardTitle>
              <CardDescription>
                Cost and usage by surface and job type — chats, projects, managed agents, meeting minutes, file processing and utility calls.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={surfaceColumns}
                data={data.surfaces}
                getRowKey={(row) => `${row.surface}:${row.model}`}
                searchFilter={(row, q) =>
                  row.surface.toLowerCase().includes(q) || row.model.toLowerCase().includes(q)
                }
                searchPlaceholder="Search surfaces or models…"
                itemLabel="row"
                emptyMessage="No usage in this period."
              />
            </CardContent>
          </Card>

          {/* ── Top users ─────────────────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Top users of models</CardTitle>
              <CardDescription>
                Top 25 by estimated cost, then tokens. Only aggregate counts are shown — never chat content.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={userColumns}
                data={data.topUsers}
                getRowKey={(row) => row.userId}
                searchFilter={(row, q) => row.name.toLowerCase().includes(q)}
                searchPlaceholder="Search users…"
                itemLabel="user"
                emptyMessage="No usage in this period."
              />
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}
